import SalesLayOut from "../SalesLayOut";
import { useDateRange } from "../DateRangeContext";
import {
    UserRound,
    Volleyball,
    Star,
    AlignStartVertical,
    EllipsisVertical,
    TrendingUp,
    DollarSign,
} from "lucide-react";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LabelList,
} from "recharts";
import { useGetLeads, useUser, useGetPermissions } from "../../Use-auth";
import { useMemo } from "react";
const chartConfigs = {
    daily: {
        leads: {
            title: "Today's Leads",
            description: "Leads for today",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        leadStatus: {
            title: "Today's Lead Status",
            description: "Lead status breakdown for today",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        budget: {
            title: "Today's Budget",
            description: "Total budget for today",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
        },
    },
    weekly: {
        leads: {
            title: "Weekly Leads",
            description: "Leads for this week",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        leadStatus: {
            title: "Weekly Lead Status",
            description: "Lead status breakdown for this week",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        budget: {
            title: "Weekly Budget",
            description: "Total budget for this week",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
        },
    },
    monthly: {
        leads: {
            title: "Monthly Leads",
            description: "Leads for this month",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        leadStatus: {
            title: "Monthly Lead Status",
            description: "Lead status breakdown for this month",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        budget: {
            title: "Monthly Budget",
            description: "Total budget for this month",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
        },
    },
    yearly: {
        leads: {
            title: "Yearly Leads",
            description: "Leads for this year",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        leadStatus: {
            title: "Yearly Lead Status",
            description: "Lead status breakdown for this year",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
        },
        budget: {
            title: "Yearly Budget",
            description: "Total budget for this year",
            icon: <AlignStartVertical className="w-16 h-16 mx-auto text-orange-500 mb-2" />,
        },
    },
};

export default function SalesDashboard() {
    const { start, end, rangeType } = useDateRange();
    const { data: response } = useGetLeads();
    const { data: user } = useUser();

    const allLeads = useMemo(() => {
        if (!Array.isArray(response?.data)) return [];

        return response.data.filter(
            (lead) => lead.userId === user?.userId
        );
    }, [response, user]);

    const totalLeads = Array.from(new Map(allLeads.map((l) => [l.id || l._id, l])).values()).filter(
        (lead) => {
            if (!start || !end) return true;
            const d = new Date(lead.createdAt);
            return d >= start && d <= end;
        }
    );

    const totalCount = totalLeads.length;
    const qualifiedCount = totalLeads.filter((l) => l.leadStatus === "Qualified").length;
    const wonCount = totalLeads.filter((l) => l.leadStatus === "Won").length;
    const activeCount = totalLeads.filter((l) => !["Won", "Lost"].includes(l.leadStatus)).length;
    const totalBudget = totalLeads.reduce((sum, lead) => {
        const leadBudget = (lead.projects || []).reduce(
            (s, project) => s + Number(project.budget || 0),
            0
        );
        return sum + leadBudget;
    }, 0);
    const { data: existingPermissions, refetch } = useGetPermissions();

    const currentUserPermissions = existingPermissions?.find(
        (p) => p.userId === user?.userId
    );

    const canViewHome =
        currentUserPermissions?.employees?.home_view === true;

    // Pre-fill chart data for correct X-axis labels
    let chartData = [];

    if (rangeType === "daily") {
        chartData = [{ day: "Today", leads: 0, total: 0 }];
    } else if (rangeType === "weekly") {
        const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        chartData = weekdays.map((day) => ({ day, leads: 0, total: 0 }));
    } else if (rangeType === "monthly") {
        const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
        chartData = Array.from({ length: daysInMonth }, (_, i) => ({ day: (i + 1).toString(), leads: 0, total: 0 }));
    } else if (rangeType === "yearly") {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        chartData = months.map((month) => ({ day: month, leads: 0, total: 0 }));
    }

    // Fill chart data with actual leads
    totalLeads.forEach((lead) => {
        const d = new Date(lead.createdAt);
        let key;
        if (rangeType === "daily") key = "Today";
        else if (rangeType === "weekly") key = d.toLocaleDateString("en-US", { weekday: "short" });
        else if (rangeType === "monthly") key = d.getDate().toString();
        else if (rangeType === "yearly") key = d.toLocaleDateString("en-US", { month: "short" });

        const entry = chartData.find((c) => c.day === key);
        if (entry) {
            entry.leads += 1;
            const leadTotalBudget = (lead.projects || []).reduce(
                (sum, project) => sum + Number(project.budget || 0),
                0
            );
            entry.total += leadTotalBudget;
        }
    });

    let leadStatusChartData = [];

    if (rangeType === "daily") {
        leadStatusChartData = [{ day: "Today", qualified: 0, won: 0, active: 0 }];
    }
    else if (rangeType === "weekly") {
        const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        leadStatusChartData = days.map(d => ({
            day: d,
            qualified: 0,
            won: 0,
            active: 0,
        }));
    }
    else if (rangeType === "monthly") {
        const daysInMonth = new Date(
            start.getFullYear(),
            start.getMonth() + 1,
            0
        ).getDate();

        leadStatusChartData = Array.from({ length: daysInMonth }, (_, i) => ({
            day: (i + 1).toString(),
            qualified: 0,
            won: 0,
            active: 0,
        }));
    }
    else if (rangeType === "yearly") {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        leadStatusChartData = months.map(m => ({
            day: m,
            qualified: 0,
            won: 0,
            active: 0,
        }));
    }

    totalLeads.forEach(lead => {

        const d = new Date(lead.createdAt);

        let key;

        if (rangeType === "daily") key = "Today";
        else if (rangeType === "weekly") key = d.toLocaleDateString("en-US", { weekday: "short" });
        else if (rangeType === "monthly") key = d.getDate().toString();
        else if (rangeType === "yearly") key = d.toLocaleDateString("en-US", { month: "short" });

        const entry = leadStatusChartData.find(e => e.day === key);

        if (!entry) return;

        if (lead.leadStatus === "Qualified") entry.qualified += 1;
        else if (lead.leadStatus === "Won") entry.won += 1;
        else if (!["Lost"].includes(lead.leadStatus)) entry.active += 1;
    });

    return (
        <SalesLayOut>
            <div className=" p-2 rs:p-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
                    <Card title="Total Contacts" value={canViewHome ? totalCount : '-'} icon={<UserRound className="w-12 h-12 opacity-80" />} bg="from-blue-500 to-blue-600" />
                    <Card title="Leads" value={canViewHome ? qualifiedCount : '-'} icon={<Volleyball className="w-12 h-12 opacity-80" />} bg="from-green-500 to-green-600" />
                    <Card title="Active Contacts" value={canViewHome ? activeCount : '-'} icon={<Star className="w-12 h-12 opacity-80" />} bg="from-purple-500 to-purple-600" />
                    <Card title="Total Budget" value={canViewHome ? `$${totalBudget}` : '-'} icon={<TrendingUp className="w-12 h-12 opacity-80" />} bg="from-yellow-500 to-yellow-600" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4 sm:mb-8">
                    <div className="bg-white rounded-lg sm:rounded-2xl p-2 sm:p-6 shadow-lg border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-[14px] sm:text-xl text-gray-800">Recent Requests</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full w-full text-xs sm:text-sm">
                                <thead className="text-gray-600 border-b border-gray-200">
                                    <tr>
                                        <th className="text-left px-1 sm:px-2 py-2">S/N</th>
                                        <th className="text-left px-1 sm:px-2 py-2">Client Name</th>
                                        <th className="text-left px-1 sm:px-2 py-2 hidden zs:table-cell">Date</th>
                                        <th className="text-left px-1 sm:px-2 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {canViewHome && totalLeads.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                        .slice(0, 4).map((lead, index) => (
                                            <tr key={lead._id || index} className="border-b border-gray-200 last:border-b-0">
                                                <td className="text-left px-1 sm:px-2 py-2">{index + 1}</td>
                                                <td className="text-left px-1 sm:px-2 py-2">{lead.firstName || "N/A"}</td>
                                                <td className="text-left px-1 sm:px-2 py-2 hidden zs:table-cell">{new Date(lead.createdAt).toLocaleDateString()}</td>
                                                <td
                                                    className={`text-left inline-block my-[4px]  px-2 sm:px-2 py-2 font-medium rounded-full text-xs text-center ${{
                                                        New: "bg-green-100 text-green-800",
                                                        Contacted: "bg-yellow-100 text-yellow-800",
                                                        Qualified: "bg-blue-100 text-blue-800",
                                                        "Proposal Sent": "bg-purple-100 text-purple-800",
                                                        Won: "bg-emerald-100 text-emerald-800",
                                                        Lost: "bg-red-100 text-red-800",
                                                    }[lead.leadStatus] || "bg-gray-100 text-gray-800"
                                                        }`}
                                                >
                                                    {lead.leadStatus}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                    </div>
                    <div className="bg-white rounded-lg sm:rounded-2xl p-2 sm:p-6 shadow-lg border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-[14px] sm:text-xl text-gray-800">Budget History</h3>
                            <EllipsisVertical className="w-6 h-6 text-gray-400 cursor-pointer" />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full w-full text-xs sm:text-sm">
                                <thead className="text-gray-600 border-b border-gray-200 bg-gray-50">
                                    <tr>
                                        <th className="text-left px-1 sm:px-2 py-2">S/N</th>
                                        <th className="text-left px-1 sm:px-2 py-2">Lead Title</th>
                                        <th className="text-left px-1 sm:px-2 py-2">Client Name</th>
                                        <th className="text-left px-1 sm:px-2 py-2 hidden zs:table-cell">Budgeted</th>
                                        <th className="text-left px-1 sm:px-2 py-2 hidden zx:table-cell">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {canViewHome && totalLeads
                                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                        .slice(0, 4)
                                        .map((lead, index) => {
                                            const totalBudget = (lead.projects || []).reduce(
                                                (sum, project) => sum + (Number(project.budget) || 0),
                                                0
                                            );
                                            return (
                                                <tr
                                                    key={lead._id || index}
                                                    className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
                                                >
                                                    <td className="text-left px-1 sm:px-2 py-2">
                                                        {index + 1}
                                                    </td>

                                                    <td className="text-left px-1 sm:px-2 py-2 max-w-[180px] truncate">
                                                        {lead.projects?.[0]?.title || "N/A"}
                                                    </td>

                                                    <td className="text-left px-1 sm:px-2 py-2 max-w-[150px] truncate">
                                                        {lead.firstName || "N/A"}
                                                    </td>

                                                    <td className="text-left px-1 sm:px-2 py-2 hidden zs:table-cell">
                                                        ${totalBudget}
                                                    </td>

                                                    <td className="text-left px-1 sm:px-2 py-2 hidden zx:table-cell">
                                                        {new Date(lead.createdAt).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ">
                    <ChartCard
                        title={chartConfigs[rangeType].leads.title}
                        icon={chartConfigs[rangeType].leads.icon}
                        description={chartConfigs[rangeType].leads.description}
                    >
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={canViewHome ? chartData : []} margin={{ top: 20, right: 10, left: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                                <YAxis allowDecimals={false} domain={[0, "dataMax"]} tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => [`${value}`, "Leads"]} />
                                <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={2} name="leads"></Line>
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                        title={chartConfigs[rangeType].leadStatus.title}
                        icon={chartConfigs[rangeType].leadStatus.icon}
                        description={chartConfigs[rangeType].leadStatus.description}
                    >
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={canViewHome ? leadStatusChartData : []} margin={{ top: 20, right: 10, left: 5, bottom: 5 }} >
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Line type="monotone" dataKey="qualified" stroke="#3B82F6" strokeWidth={2} name="Qualified" />
                                <Line type="monotone" dataKey="won" stroke="#10B981" strokeWidth={2} name="Won" />
                                <Line type="monotone" dataKey="active" stroke="#F59E0B" strokeWidth={2} name="Active" />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard
                        title={chartConfigs[rangeType].budget.title}
                        icon={chartConfigs[rangeType].budget.icon}
                        description={chartConfigs[rangeType].budget.description}
                    >
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={canViewHome ? chartData : []} margin={{ top: 20, right: 10, left: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => `$${value}`} />
                                <Line type="monotone" dataKey="total" stroke="#F97316" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </div>
        </SalesLayOut>
    );
}

const Card = ({ title, value, icon, bg }) => (
    <div
        className={`bg-gradient-to-r ${bg} text-white rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg flex justify-between items-center w-full group`}
    >
        <div className="flex items-center justify-between w-full">
            <div>
                <p className="text-[12px] sm:text-sm xl:text-base font-medium opacity-80">
                    {title}
                </p>
                <p className="text-[14px] sm:text-lg xl:text-3xl font-bold">
                    {value}
                </p>
            </div>
            <div
                className="w-6 h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80
                flex items-center justify-center
                [&_svg]:w-full [&_svg]:h-full
                group-hover:scale-125 transition-transform duration-300"
            >
                {icon}
            </div>
        </div>
    </div>
);

const ChartCard = ({ title, icon, description, children }) => (
    <div className="bg-white rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg border border-gray-100">
        <div className="flex justify-between items-center mb-2 sm:mb-4">
            <h3 className="font-bold text-[14px] sm:text-lg xl:text-xl text-gray-800">
                {title}
            </h3>
            {/* <EllipsisVertical className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400 cursor-pointer" /> */}
        </div>

        <div className="text-center mb-2 sm:mb-4">
            <div className="mb-1 sm:mb-2">{icon}</div>
            <p className="text-[14px] sm:text-2xl font-semibold text-gray-700">
                {title}
            </p>
            <p className="text-[10px] sm:text-sm text-gray-500">
                {description}
            </p>
        </div>

        {children}
    </div>
);